import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { FileText, Printer } from 'lucide-react';
import { formatCATDate } from '../../utils/timezone';
import { downloadMonthlySummaryCSV, printMonthlySummary } from './exportUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export default function MonthlySummaryModal({ open, onOpenChange, canViewAllData, isSuperAdmin, hospitalName, hospitalStats, authCfg }) {
  const [month, setMonth] = useState(currentMonth);
  const [hospital, setHospital] = useState('ALL');
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async (ym, hosp) => {
    setLoading(true);
    setError('');
    try {
      const [yStr, mStr] = (ym || '').split('-');
      const year = Number(yStr);
      const m = Number(mStr);
      if (!year || !m) {
        setClaims([]);
        return;
      }
      const params = { year, month: m, limit: 100000, skip: 0 };
      if (isSuperAdmin && hosp && hosp !== 'ALL') params.hospital_name = hosp;
      const res = await axios.get(`${API}/claims/monthly-summary`, { ...authCfg, params });
      setClaims(res.data?.claims || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load monthly claims summary');
      setClaims([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load(month, hospital);
    // eslint-disable-next-line
  }, [open, month, hospital]);

  const hospLabelForCsv = isSuperAdmin ? (hospital || 'ALL') : (hospitalName || 'hospital');
  const hospLabelForPrint = isSuperAdmin ? (hospital === 'ALL' ? 'All Hospitals' : hospital) : hospitalName;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-green-700" />
            Monthly Claims Summary
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-col md:flex-row gap-3 md:items-end">
            <div>
              <Label className="text-sm">Select Month</Label>
              <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1" data-testid="monthly-summary-month" />
            </div>

            {canViewAllData && (
              <div>
                <Label className="text-sm">Hospital Filter</Label>
                <Select value={hospital} onValueChange={setHospital}>
                  <SelectTrigger className="mt-1 w-[260px]"><SelectValue placeholder="All Hospitals" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Hospitals</SelectItem>
                    {Object.keys(hospitalStats || {}).map((h) => (
                      <SelectItem key={h} value={h}>{h}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex gap-2 md:ml-auto">
              <Button onClick={() => printMonthlySummary(claims, month, hospLabelForPrint)} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
              <Button variant="outline" onClick={() => downloadMonthlySummaryCSV(claims, month, hospLabelForCsv)} disabled={loading}>
                Download CSV
              </Button>
            </div>
          </div>

          {error && <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

          {loading ? (
            <div className="text-center py-8 text-gray-600">Loading monthly summary...</div>
          ) : (
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">#</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Claim ID</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Patient</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Date</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Serial No</th>
                    <th className="text-right p-3 text-sm font-semibold text-gray-700">Amount (USD)</th>
                    <th className="text-center p-3 text-sm font-semibold text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {claims.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="p-4 text-center text-gray-500">No claims found for this month.</td>
                    </tr>
                  ) : (
                    claims.map((c, idx) => (
                      <tr key={`${c.claim_id}-${idx}`} className="border-b hover:bg-gray-50">
                        <td className="p-3 text-sm text-gray-700">{idx + 1}</td>
                        <td className="p-3 text-sm font-medium text-gray-800">{c.claim_id}</td>
                        <td className="p-3 text-sm text-gray-700">{c.patient_name}</td>
                        <td className="p-3 text-sm text-gray-600">{c.timestamp ? formatCATDate(c.timestamp) : ''}</td>
                        <td className="p-3 text-sm text-gray-700">{c.patient_serial_number}</td>
                        <td className="p-3 text-sm text-gray-800 text-right font-medium">{Number(c.total_claim_amount || 0).toFixed(2)}</td>
                        <td className="p-3 text-center">
                          <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                            c.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800'
                              : c.status === 'PAID' ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {c.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
